import { FC, useState } from "react";
import {
	CalendarItem,
	Class,
	CourseData,
	DayCourseData,
	DayEventData,
	EventData,
	ViewOptions,
	WEEKLY_OR_CUSTOM,
} from "../types";
import "./MonthlyView.css";
import {
	areSameDay,
	compareStartTimes,
	findClassOnThisDay,
	getDate,
} from "../helpers";
import { EventDetails } from "../EventDetails";
import { EventDetailsVariant } from "../EventDetails/EventDetails";
import { Modal } from "../Modal";

interface MonthlyViewProps {
	calendarItems: CalendarItem[];
	view: ViewOptions;
}

const getDaysInMonth = (year: number, month: number): number => {
	return new Date(year, month + 1, 0).getDate();
};

const MonthlyView: FC<MonthlyViewProps> = ({ calendarItems, view }) => {
	const currentDate = new Date();
	const [selectedDate, setSelectedDate] = useState<Date>(currentDate);
	const [selectedEvent, setSelectedEvent] = useState<CalendarItem | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalDate, setModalDate] = useState<Date>(selectedDate);

	const daysInMonth = getDaysInMonth(
		selectedDate.getFullYear(),
		selectedDate.getMonth()
	);

	const firstDayOfMonth = new Date(
		selectedDate.getFullYear(),
		selectedDate.getMonth(),
		1
	);

	const startingDay = firstDayOfMonth.getDay();

	const calendarDays = [];
	const monthCalendarItemsList = [];

	// Fill in the days before the first day of the month
	for (let i = -6; i < startingDay; i++) {
		calendarDays.push(
			<div
				key={`empty-${i}`}
				className="empty-day"
			></div>
		);
	}

	const handleEventClick = (calendarItem: CalendarItem, dayDate: Date) => {
		setSelectedEvent(calendarItem);
		setIsModalOpen(true);
		setModalDate(dayDate);
	};

	const allCourses = calendarItems.filter((calendarItem) => {
		return !calendarItem.event;
	}) as CourseData[];

	const allEvents = calendarItems.filter((calendarItem) => {
		return calendarItem.event;
	}) as EventData[];

	// Fill in the days of the month
	for (let i = 1; i <= daysInMonth; i++) {
		const dayDate = new Date(
			selectedDate.getFullYear(),
			selectedDate.getMonth(),
			i
		);

		const dayEvents = allEvents.filter((event: DayEventData) => {
			if (event.acf.start_date_time && event.acf.end_date_time) {
				const startDate = new Date(getDate(event.acf.start_date_time));
				const endDate = new Date(getDate(event.acf.end_date_time));

				event.day_start_date_time = new Date(event.acf.start_date_time);
				event.day_end_date_time = new Date(event.acf.end_date_time);

				return (
					areSameDay(startDate, dayDate) ||
					areSameDay(endDate, dayDate) ||
					(dayDate > startDate && dayDate < endDate)
				);
			}
			return false;
		});

		// Filter out events that take longer than one day
		const dayEventsForList = dayEvents.filter((event) => {
			const startDate = new Date(getDate(event.acf.start_date_time));
			if (dayDate > startDate) {
				return false;
			}
			return true;
		});

		const dayCustomCourses = allCourses.filter((course: DayCourseData) => {
			if (course.acf.weekly_or_custom === WEEKLY_OR_CUSTOM.CUSTOM) {
				if (course.acf.custom_courses && course.acf.custom_courses.length > 0) {
					const customCourse: Class[] = course.acf.custom_courses;
					return customCourse.some((item) => {
						const startDate = new Date(getDate(item.start_date_time));
						if (areSameDay(startDate, dayDate)) {
							course.day_start_date_time = new Date(item.start_date_time);
							course.day_end_date_time = new Date(item.end_date_time);
							return true;
						}
						return false;
					});
				}
			}
			return false;
		});

		const dayWeeklyCourses = allCourses.filter((course) => {
			if (course.acf.weekly_or_custom === WEEKLY_OR_CUSTOM.WEEKLY) {
				return findClassOnThisDay(course, dayDate);
			}
			return false;
		});

		const dayEventsAndCourses = [
			...(dayEvents as EventData[]),
			...(dayCustomCourses as CourseData[]),
			...(dayWeeklyCourses as CourseData[]),
		];

		dayEventsAndCourses.sort(compareStartTimes);

		const dayEventsAndCoursesForList = [...dayEventsForList, ...dayWeeklyCourses];

		dayEventsAndCoursesForList.sort(compareStartTimes);

		monthCalendarItemsList.push(
			dayEventsAndCoursesForList.map((event) => (
				<li
					key={event.id}
					className="list-style-none list-upcoming-event"
				>
					<EventDetails
						event={event}
						variant={EventDetailsVariant.LIST}
						dayDate={dayDate}
					/>
				</li>
			))
		);

		calendarDays.push(
			<div
				key={i}
				className="calendar-day"
			>
				<div className="day-number">{i}</div>
				<ul className="scb-day-events-list">
					{dayEventsAndCourses.map((calendarItem) => (
						<li
							key={calendarItem.id}
							className="scb-day-events-list__item"
							onClick={handleEventClick.bind(null, calendarItem, dayDate)}
						>
							<EventDetails
								event={calendarItem}
								variant={EventDetailsVariant.DAY}
								dayDate={dayDate}
							/>
						</li>
					))}
				</ul>
			</div>
		);
	}

	return (
		<div className="calendar">
			<Modal
				isOpen={isModalOpen}
				closeModal={() => setIsModalOpen(false)}
				event={selectedEvent}
				modalDate={modalDate}
			/>
			<div className="calendar-header">
				<button
					className="scb-calendar-button"
					onClick={() =>
						setSelectedDate(
							new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1)
						)
					}
				>
					<div>&#x2190;</div>
					<div>Vorheriger Monat</div>
				</button>
				<h3 className="scb-calendar-month-heading">
					{selectedDate.toLocaleDateString("de-DE", {
						month: "long",
						year: "numeric",
					})}
				</h3>
				<button
					className="scb-calendar-button"
					onClick={() =>
						setSelectedDate(
							new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1)
						)
					}
				>
					<div>Nächster Monat</div>
					<div>&#x2192;</div>
				</button>
			</div>

			{view === ViewOptions.MONTHLY && (
				<div className="scb-calendar-wrapper">
					<div className="scb-calendar-inner-wrapper">
						<div className="days-header calendar-grid">
							<div>Mo</div>
							<div>Di</div>
							<div>Mi</div>
							<div>Do</div>
							<div>Fr</div>
							<div>Sa</div>
							<div>So</div>
						</div>
						<div className="days-wrapper calendar-grid">{calendarDays}</div>
					</div>
				</div>
			)}
			<div>
				{view === ViewOptions.LIST && (
					<ul className="scb-list-upcoming-events">{monthCalendarItemsList}</ul>
				)}
			</div>
		</div>
	);
};

export default MonthlyView;
